import { motion, useAnimationControls, useReducedMotion } from "framer-motion";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { CAMERA_DURATION, CAMERA_EASE, DETAIL_START_SCALE, ZOOM_IN_SCALE } from "./motion";

export type ZoomTarget = { key: string; origin: { x: number; y: number } };

type Props = {
  /** null = show the overview; otherwise the camera dives into target.origin and shows its detail. */
  target: ZoomTarget | null;
  overview: ReactNode;
  detail: (key: string) => ReactNode;
};

const layer = { position: "absolute", inset: 0 } as const;

/**
 * Two stacked layers in stage coordinates (1280×720). Zooming in, the overview scales up around the
 * node's centre while fading out and the detail grows from the same point; zooming out reverses it.
 * Moving between two details passes back through the overview so the audience sees where they are.
 */
export function ZoomStage({ target, overview, detail }: Props) {
  const reduced = useReducedMotion() ?? false;
  const ov = useAnimationControls();
  const dt = useAnimationControls();
  const [shown, setShown] = useState<ZoomTarget | null>(target);
  const shownRef = useRef<ZoomTarget | null>(target);
  const run = useRef(0);

  // Snap to the initial state without animating (e.g. arriving on a slide already zoomed in).
  useEffect(() => {
    ov.set(target ? { scale: reduced ? 1 : ZOOM_IN_SCALE, opacity: 0 } : { scale: 1, opacity: 1 });
    dt.set(target ? { scale: 1, opacity: 1 } : { scale: reduced ? 1 : DETAIL_START_SCALE, opacity: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-run only when the destination really changes. Re-renders (a live session's position echo,
  // a tally) hand us an equal target object; treating that as a new move used to cancel a zoom halfway,
  // leaving the overview showing but unclickable.
  const targetKey = target?.key ?? null;
  const ox = target?.origin.x;
  const oy = target?.origin.y;

  useEffect(() => {
    const target: ZoomTarget | null =
      targetKey === null ? null : { key: targetKey, origin: { x: ox ?? 640, y: oy ?? 360 } };
    const current = shownRef.current;
    if (current?.key === target?.key) return; // already there, or already on the way: don't interrupt
    const id = ++run.current;
    const transition = { duration: reduced ? CAMERA_DURATION / 2 : CAMERA_DURATION, ease: CAMERA_EASE };
    const zoomedOut = { scale: 1, opacity: 1 };
    const zoomedIn = { scale: reduced ? 1 : ZOOM_IN_SCALE, opacity: 0 };
    const detailIn = { scale: 1, opacity: 1 };
    const detailOut = { scale: reduced ? 1 : DETAIL_START_SCALE, opacity: 0 };

    (async () => {
      if (current) {
        await Promise.all([ov.start(zoomedOut, transition), dt.start(detailOut, transition)]);
        if (id !== run.current) return;
      }
      shownRef.current = target;
      setShown(target);
      if (!target) return;
      // Let React apply the new transform-origin while both layers are at rest, then dive.
      await new Promise(requestAnimationFrame);
      if (id !== run.current) return;
      dt.set(detailOut);
      await Promise.all([ov.start(zoomedIn, transition), dt.start(detailIn, transition)]);
    })();
  }, [targetKey, ox, oy, reduced, ov, dt]);

  const origin = shown ? `${shown.origin.x}px ${shown.origin.y}px` : "50% 50%";
  const zoomed = shown !== null && target !== null && shown.key === target.key;

  return (
    <>
      <motion.div
        style={{ ...layer, transformOrigin: origin, pointerEvents: zoomed ? "none" : "auto" }}
        animate={ov}
        aria-hidden={zoomed}
        data-layer="overview"
      >
        {overview}
      </motion.div>
      <motion.div
        style={{ ...layer, transformOrigin: origin, pointerEvents: zoomed ? "auto" : "none" }}
        animate={dt}
        aria-hidden={!zoomed}
        data-layer="detail"
      >
        {shown ? detail(shown.key) : null}
      </motion.div>
    </>
  );
}
